import mongoose from "mongoose";
import toast from "react-hot-toast";
import { IAddress } from "../models/LeadData.model";
import axiosSetup from "../utils/axiosSetup";
import { ItrackingDetails } from "../models/Order.model";
import ResponseModel from "../models/response.model";

export const handleLeadOrder = async (
  category: string,
  name: string,
  mobileNumber: string,
  email: string,
  shippingAddress: IAddress,
  billingAddress: IAddress,
  leadId: string,
  selectedGoods: any,
  gstin: string,
  quotationId: string,
  transportPrice: number,
  discountPrice: number,
  grandTotal: number,
) => {
  if (!category) {
    toast.error("Please select a category.");
    return;
  }
  if (!name) {
    toast.error("Please fill in Name");
    return;
  }
  if (!mobileNumber) {
    toast.error("Mobile Number is required.");
  }

  if (
    !shippingAddress.line1 ||
    !shippingAddress.city ||
    !shippingAddress.state ||
    !shippingAddress.pincode
  ) {
    toast.error("Please fill in the complete Shipping Address.");
    return;
  }

  if (
    shippingAddress.pincode &&
    !/^\d{6}$/.test(shippingAddress.pincode.trim())
  ) {
    toast.error("Pincode must be exactly 6 digits.");
    return;
  }

  if (!gstin) {
    toast.error("GSTIN is required.");
    return;
  }

  if (
    gstin &&
    !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(
      gstin.trim(),
    )
  ) {
    toast.error("Please enter a valid GSTIN.");
    return;
  }

  const res = await axiosSetup.post(
    `leads/order/${leadId}`,
    {
      name,
      email,
      mobileNumber,
      billingAddress,
      shippingAddress,
      category,
      goods: selectedGoods,
      gstin: gstin,
      quotationId,
      transportPrice: transportPrice,
      discountPrice: discountPrice,
      totalAmount: grandTotal,
    },
    {},
  );

  return res.data;
};

export const saveOnOrderModel = async (orderData: {
  leadId: string;
  goods: { productId: string; quantity: number }[];
  gstin: string;
  customerId: string;
  quotationId?: string;
}) => {
  const response = await axiosSetup.post("/orders/create", orderData);
  return response.data.data;
};

export const removeAssignedWarehouseApi = async (orderId: string) => {
  const response = await axiosSetup.patch(
    `/orders/pending-dispatch/${orderId}/reset-assignment`,
  );
  return response.data;
};

export const moveOrderToCompletedApi = async (
  orderId: string,
): Promise<ResponseModel> => {
  const response = await axiosSetup.patch(
    `/orders/move-to-completed/${orderId}`,
  );
  return response.data as ResponseModel;
};

export const getOrdersRoleBased = async (
  status: string,
  search?: string,
  page?: number,
  createdBy?: string[],
) => {
  const params: any = {};
  if (search) params.search = search;
  if (status) params.status = status;
  if (page) params.page = page;
  if (createdBy) params.createdBy = createdBy;
  const res = await axiosSetup.get(`/orders/status/${status}`, {
    params: params,
  });
  return res.data as ResponseModel;
};

export const getAllOrdersForAccountsAPI = async (params: any) => {
  const res = await axiosSetup.get(`orders/get-all-orders-payment`, { params });
  return res.data as ResponseModel;
};

export const getCustomerOrder = async (leadId: string) => {
  const res = await axiosSetup.get(`orders/${leadId}`);
  return res.data.data;
};

export const addTrackingDetails = async (
  orderId: string,
  goods: { productId: string; quantity: number }[],
  trackingDetails: ItrackingDetails[],
) => {
  try {
    // Validate orderId
    if (!mongoose.Types.ObjectId.isValid(orderId)) {
      toast.error(`Invalid orderId: ${orderId}`);
      throw new Error(`Invalid orderId: ${orderId}`);
    }

    const res = await axiosSetup.post(`orders/addTrackingDetails/`, {
      orderId,
      goods,
      trackingDetails,
    });

    return res.data as ResponseModel;
  } catch (error: any) {
    console.error("Error adding tracking details:", error);
    const errorMessage =
      error.response?.data?.message ||
      "Unable to complete dispatch. Check stock availability and serial numbers, then try again.";
    toast.error(errorMessage);
    throw error;
  }
};

export const updateOrderStatusByAccount = async (
  id: string,
  status: string,
) => {
  try {
    const res = await axiosSetup.put(`orders/updateOrderStatus/`, {
      id,
      status,
    });

    return res.data;
  } catch (error: any) {
    console.error("Error updating order status:", error);

    const errorMessage =
      error.response?.data?.message ||
      "Failed to update order status. Please try again.";

    toast.error(errorMessage);

    throw error;
  }
};

export const updatePaymentDetails = async (formData: FormData) => {
  const response = await axiosSetup.post(
    "orders/addPaymentDetails/",
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
  return response.data.data;
};

export const validatePaymentByAccount = async (
  orderId: string,
  utrNumbers?: string[],
  screenshots?: string[],
) => {
  try {
    const res = await axiosSetup.patch(`orders/verifyPaymentByAccount`, {
      orderId,
      utrNumbers,
      screenshots,
    });
    return res.data.data;
  } catch (error: any) {
    console.error("Error updating payment details:", error);
  }
};

export const cancelledOrder = async (orderId: string) => {
  const res = await axiosSetup.patch(`orders/cancel-order/${orderId}`);
  return res.data;
};

export const getPendingWarehouseAssignment = async ({
  page = 1,
  search = "",
  startDate = "",
  endDate = "",
}) => {
  const res = await axiosSetup.get(
    `/orders/warehouse/pending-assignment?page=${page}&limit=10&search=${search}&startDate=${startDate}&endDate=${endDate}`,
  );
  return res.data.data;
};

export const getPendingDispatchOrders = async ({
  page = 1,
  search = "",
  startDate = "",
  endDate = "",
}) => {
  const res = await axiosSetup.get(
    `/orders/warehouse/pending-dispatch?page=${page}&limit=10&search=${search}&startDate=${startDate}&endDate=${endDate}`,
  );
  return res.data.data;
};

export const getPartiallyDispatchedOrders = async ({
  page = 1,
  search = "",
  startDate = "",
  endDate = "",
}) => {
  const res = await axiosSetup.get(
    `/orders/warehouse/partially-dispatched?page=${page}&limit=10&search=${search}&startDate=${startDate}&endDate=${endDate}`,
  );
  return res.data.data;
};

export const getFullyDispatchedOrders = async ({
  page = 1,
  search = "",
  startDate = "",
  endDate = "",
}) => {
  const res = await axiosSetup.get(
    `/orders/warehouse/fully-dispatched?page=${page}&limit=10&search=${search}&startDate=${startDate}&endDate=${endDate}`,
  );
  return res.data as ResponseModel;
};

export const getDispatchedOrders = async ({
  page = 1,
  search = "",
  startDate = "",
  endDate = "",
}) => {
  const res = await axiosSetup.get(
    `/orders/warehouse/dispatched?page=${page}&limit=10&search=${search}&startDate=${startDate}&endDate=${endDate}`,
  );
  return res.data as ResponseModel;
};

export const assignWarehouseToOrder = async (
  orderId: string,
  warehouseId: string,
  adjustments: any[],
  transportPrice: string,
  goods: any[],
) => {
  const res = await axiosSetup.patch(`/orders/${orderId}/assign-warehouse`, {
    warehouseId,
    adjustments,
    transportPrice: Number(transportPrice),
    goods,
  });
  return res.data.data;
};

export const creditPaymentApi = async (orderId: string) => {
  const response = await axiosSetup.post("/orders/credit/request", { orderId });
  return response.data.data;
};

export const getPendingCreditRequestsApi = async (params: any) => {
  const response = await axiosSetup.get("/orders/credit/requested", { params });
  return response.data;
};

export const getCreditRequestHistoryApi = async (params: any) => {
  const response = await axiosSetup.get("/orders/credit/history", { params });
  return response.data;
};

export const approveCreditRequestApi = async (data: {
  orderId: string;
  status: "approved" | "rejected" | "cancelled";
  remark: string;
}) => {
  const response = await axiosSetup.post(
    "/orders/manager/credit/approval",
    data,
  );
  return response.data;
};

export const updateOrderAddressApi = async (
  orderId: string,
  shippingAddress: any,
) => {
  const response = await axiosSetup.patch(`/orders/${orderId}/address`, {
    shippingAddress,
  });
  return response.data;
};

export const checkStockAvailabilityApi = async (
  orderId: string,
  warehouseId: string,
  goods: any[],
) => {
  const { data } = await axiosSetup.post(
    `/orders/check-availability`,
    { goods },
    {
      params: { orderId, warehouseId },
    },
  );
  return data.data;
};

export const getEpcOrdersApi = async ({
  page = 1,
  search = "",
  startDate = "",
  endDate = "",
}) => {
  const res = await axiosSetup.get(
    `/orders/warehouse/epc-orders?page=${page}&limit=10&search=${search}&startDate=${startDate}&endDate=${endDate}`,
  );
  return res.data.data;
};
